import { useEffect, useId, useRef, useState } from 'react'
import Icon from './Icon.jsx'
import {
  addDays,
  addMonths,
  firstOfMonth,
  formatDate,
  formatMonth,
  isoWeekday,
  lastOfMonth,
  monthGrid,
  monthOf,
  weekdayName,
} from '../lib/dates.js'

/**
 * A month of days to pick from, drawn from this app's own date arithmetic.
 *
 * This used to be react-day-picker. Dropping it was not about the library being poor — it is
 * the best of its kind — but about how little was left for it to do here. None of its
 * stylesheet was imported, so every pixel was already ours. Both range pickers overrode its
 * range selection, because its "stretch the range to meet the click" behaviour silently turned
 * a moved absence into a thirteen-day one. And lib/dates.js already had monthGrid(), which
 * draws the two other calendars in this app. What genuinely remained was the keyboard grid,
 * which is the sixty lines below.
 *
 * Going without it removes three packages — the library, date-fns and @date-fns/tz — and, more
 * to the point, removes the only place in the codebase where a 'YYYY-MM-DD' string had to
 * become a Date and come back. That conversion was correct, and it was one careless edit away
 * from moving a crew's day by one, in some timezones, on some days.
 *
 * Selection SEMANTICS are deliberately not here. This draws days and reports which one was
 * activated; whether a click starts a range or finishes one belongs to the field above it,
 * which is the thing that knows what it is asking for.
 */

/**
 * The days of a month grid with the flags every calendar in this app classes them by.
 *
 * Exported because MonthGrid and the People calendar each grew their own copy of this loop,
 * and they had already drifted over whether an outside day also gets the weekend shade.
 */
export function monthDays(ym, weekStart = 1, today = null) {
  return monthGrid(ym, weekStart).map((date) => ({
    date,
    outside: monthOf(date) !== ym,
    weekend: isoWeekday(date) >= 6,
    isToday: date === today,
  }))
}

/**
 * The column headings, taken from the grid's own first row so they cannot fall out of step.
 *
 * Also exported: both other calendars kept a hard-coded Mon-to-Sun array and rotated it by
 * `weekStart` themselves, which is the same list written three times and rotated three ways.
 */
export const weekdayLabels = (ym, weekStart) => monthGrid(ym, weekStart).slice(0, 7).map(weekdayName)

const clamp = (iso, min, max) => (min && iso < min ? min : max && iso > max ? max : iso)

/**
 * The same day next month, pulled back to the last day when that month is shorter.
 *
 * PageDown from the 31st has to land somewhere, and adding thirty days lands in the wrong
 * month twice a year — the bug Board's own stepMonth already exists to avoid.
 */
function shiftMonth(iso, dir) {
  const target = addMonths(monthOf(iso), dir)
  const last = lastOfMonth(target)
  const same = `${target}-${iso.slice(8)}`
  return same > last ? last : same
}

export default function Calendar({
  defaultMonth,
  selected = null, // 'YYYY-MM-DD', or { from, to } in range mode
  mode = 'single',
  onPick,
  min,
  max,
  today,
  weekStart = 1,
  months = 1,
  showOutside = true,
}) {
  const anchorDate = (mode === 'range' ? selected?.from : selected) || defaultMonth || today
  const [month, setMonth] = useState(() => monthOf(anchorDate || ''))
  // Which day the arrow keys are on. Not the selection: moving through a calendar to look at
  // next month is not choosing anything, and a grid that selected as you arrowed through it
  // would fire onPick on the way past.
  const [focus, setFocus] = useState(() => clamp(anchorDate || firstOfMonth(monthOf(anchorDate || '')), min, max))
  const gridRef = useRef(null)
  const moved = useRef(false)
  const captionId = useId()

  // Follow the focused day into whichever month it lands in, so arrowing off the end of one
  // month turns the page rather than moving focus to a day nobody can see.
  useEffect(() => {
    const shown = Array.from({ length: months }, (_, i) => addMonths(month, i))
    if (focus && !shown.includes(monthOf(focus))) setMonth(monthOf(focus))
  }, [focus, month, months])

  // Only after a key press. Focusing the grid because the panel merely opened would take
  // focus off the trigger before anybody asked for it.
  useEffect(() => {
    if (!moved.current) return
    moved.current = false
    gridRef.current?.querySelector(`[data-date="${focus}"]`)?.focus()
  }, [focus])

  const disabled = (iso) => Boolean((min && iso < min) || (max && iso > max))

  const go = (next) => {
    if (!next) return
    moved.current = true
    setFocus(clamp(next, min, max))
  }

  const onKeyDown = (e) => {
    const k = e.key
    let next = null
    if (k === 'ArrowLeft') next = addDays(focus, -1)
    else if (k === 'ArrowRight') next = addDays(focus, 1)
    else if (k === 'ArrowUp') next = addDays(focus, -7)
    else if (k === 'ArrowDown') next = addDays(focus, 7)
    else if (k === 'Home') next = addDays(focus, -((isoWeekday(focus) - weekStart + 7) % 7))
    else if (k === 'End') next = addDays(focus, 6 - ((isoWeekday(focus) - weekStart + 7) % 7))
    else if (k === 'PageUp') next = shiftMonth(focus, -1)
    else if (k === 'PageDown') next = shiftMonth(focus, 1)
    else if (k === 'Enter' || k === ' ') {
      e.preventDefault()
      if (!disabled(focus)) onPick(focus)
      return
    } else return
    e.preventDefault()
    go(next)
  }

  /**
   * The arrows turn the page and take the keyboard's day with them.
   *
   * They have to: the effect above follows that day, so turning the page alone left it in a
   * month no longer shown and the page was turned straight back — the arrows did nothing.
   * Moving the day along is also what the keyboard expects, since tabbing into the grid
   * after paging should land in the month on screen. DOM focus stays on the arrow (`moved`
   * is not set), so a mouse user can keep clicking.
   *
   * The clamp cannot pull it back off the page: an arrow is only enabled while the month it
   * reveals has a day inside min and max (canBack / canFwd).
   */
  const stepMonth = (dir) => {
    setMonth((m) => addMonths(m, dir))
    setFocus((f) => (f ? clamp(shiftMonth(f, dir), min, max) : f))
  }
  const canBack = !min || firstOfMonth(month) > min
  const canFwd = !max || lastOfMonth(addMonths(month, months - 1)) < max

  const state = (iso) => {
    if (mode !== 'range') return { on: iso === selected }
    const { from, to } = selected || {}
    if (!from) return {}
    const last = to || from
    return {
      on: iso === from || iso === last,
      start: iso === from && from !== last,
      end: iso === last && from !== last,
      middle: iso > from && iso < last,
    }
  }

  return (
    <div className="dp-cal">
      <div className="dp-cal-head">
        <button
          type="button"
          className="dp-cal-nav"
          onClick={() => stepMonth(-1)}
          disabled={!canBack}
          aria-label="Previous month"
        >
          <Icon name="back" size={16} strokeWidth={2} />
        </button>
        <div className="dp-cal-caption" id={captionId} aria-live="polite">
          {Array.from({ length: months }, (_, i) => formatMonth(addMonths(month, i))).join(' – ')}
        </div>
        <button
          type="button"
          className="dp-cal-nav"
          onClick={() => stepMonth(1)}
          disabled={!canFwd}
          aria-label="Next month"
        >
          <Icon name="right" size={16} strokeWidth={2} />
        </button>
      </div>

      {/* One key handler for however many months are shown, so focus can walk off the last day
          of the left month into the first of the right one without a special case. */}
      <div className="dp-cal-months" ref={gridRef} onKeyDown={onKeyDown}>
        {Array.from({ length: months }, (_, i) => addMonths(month, i)).map((ym) => {
          const days = monthDays(ym, weekStart, today)
          const weeks = Array.from({ length: days.length / 7 }, (_, w) => days.slice(w * 7, w * 7 + 7))
          return (
            <div className="dp-cal-month" key={ym} role="grid" aria-labelledby={captionId}>
              <div className="dp-cal-week dp-cal-weekdays" role="row">
                {weekdayLabels(ym, weekStart).map((l) => (
                  <span key={l} className="dp-cal-weekday" role="columnheader" aria-label={l}>
                    {l}
                  </span>
                ))}
              </div>
              {weeks.map((week) => (
                <div className="dp-cal-week" role="row" key={week[0].date}>
                  {week.map((d) => {
                    if (d.outside && !showOutside) {
                      return <span key={d.date} className="dp-cal-day dp-cal-blank" role="gridcell" />
                    }
                    const s = state(d.date)
                    return (
                      <button
                        key={d.date}
                        type="button"
                        role="gridcell"
                        data-date={d.date}
                        className={[
                          'dp-cal-day',
                          d.outside ? 'outside' : '',
                          d.weekend ? 'weekend' : '',
                          d.isToday ? 'today' : '',
                          s.on ? 'on' : '',
                          s.start ? 'start' : '',
                          s.middle ? 'middle' : '',
                          s.end ? 'end' : '',
                        ].filter(Boolean).join(' ')}
                        // One tab stop for the whole grid: Tab reaches the calendar, the arrow
                        // keys move within it. Thirty-five tab stops is not navigation.
                        tabIndex={d.date === focus ? 0 : -1}
                        aria-selected={Boolean(s.on || s.middle)}
                        aria-current={d.isToday ? 'date' : undefined}
                        disabled={disabled(d.date)}
                        onFocus={() => setFocus(d.date)}
                        onClick={() => onPick(d.date)}
                        aria-label={formatDate(d.date, { withYear: true })}
                      >
                        {Number(d.date.slice(8))}
                      </button>
                    )
                  })}
                </div>
              ))}
            </div>
          )
        })}
      </div>
    </div>
  )
}
